import { ChangeDetectorRef, Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderFormSchemaFactory, BuilderUserContext, FormRootSchema, FormStateSchema, whenHasSystemRole, whenOfficeUser, whenOfficeView, whenSwitchedUser, } from '@gh/builder-shared';
import {
  BuilderPermission, PRIVILEGED_EP_USER_SEND_EMAILS_FROM_VERIFICATION, PRIVILEGED_EP_USER_UPDATE, PRIVILEGED_EP_USER_VERIFICATION, SystemRoleBit, UserAllowAccess, UserStatus, UserSystemRole, } from '@gh/builder-shared-data';
import {
  FormState, isRouteForCreationMode, MaskedTypes, TextTypes, toAppFormControl, toAppFormGroup, TYPING_DELAY, whenAllOf, whenCreateMode, whenCreateOrEditMode, whenEditMode, whenOneOf, } from '@gh/common-shared';
import { basketToFormErrors, MessageBasket } from '@gh/core-messages';
import {
  FormMode, getControl, getControlValue, markControlAsDirty, markControlAsTouched, NotificationService, setControlValue, validateControlValue, } from '@gh/core-ui';
import { coalesce, nullate } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { map$, catchError$, delay$, switchMap$ } from '@gh/rx/operators';

import { constant, isEmpty, isNil, mapValues } from 'lodash';
import { Observable } from 'rxjs';

import { UserDetailsBusinessService } from '../../resources/user-details-business.service';
import {
  getNormalizedEmailName,
  UserDetails,
  UserDetailsContact,
  UserValidateLogin,
  UserValidateLoginExistingUser,
} from '../../resources/user-details.model';

@Injectable()
export class UserDetailsFormState extends FormState<UserDetails> {
  private originalSendEmailsFrom?: string;
  /** Login value when the form was built; used to skip validate-login when unchanged in edit. */
  private originalUserEmail?: string;
  /** Office B grant path — existing user from another office, once multi-office access is ticked. */
  grantExistingUser: Maybe<UserValidateLoginExistingUser>;
  /** Login entered belongs to another office's employee (validate-login), whether or not access is ticked yet. */
  detectedExistingUser: Maybe<UserValidateLoginExistingUser>;

  constructor(private route: ActivatedRoute,
              schemaFactory: BuilderFormSchemaFactory,
              private userContext: BuilderUserContext,
              private businessService: UserDetailsBusinessService,
              private notificationService: NotificationService,
              private cdr: ChangeDetectorRef) {
    super(schemaFactory);
  }

  get allowAccessToLeads(): UserAllowAccess {
    return getControlValue(this.group, 'allowAccessToLeads');
  }

  get allowAccessToMultipleOffices(): boolean {
    return getControlValue(this.group, 'allowAccessToMultipleOffices');
  }

  get allowEmailAccessToMultipleOffices(): boolean {
    return getControlValue(this.group, 'allowEmailAccessToMultipleOffices');
  }

  get isSalesPerson(): boolean {
    return getControlValue(this.group, 'systemRoles')[UserSystemRole.Salesperson];
  }

  get userDataToTransfer(): UserAllowAccess {
    return getControlValue(this.group, 'userDataToTransfer');
  }

  get existingUser(): boolean {
    return getControlValue(this.group, 'existingUser');
  }

  get isGrantMode(): boolean {
    return Boolean(this.grantExistingUser);
  }

  /** Existing user from another office detected, but "Allow this user to sign in to more than one office" not ticked. */
  get needsMultiOfficeOptIn(): boolean {
    return Boolean(this.detectedExistingUser) && !this.isGrantMode;
  }

  /** The admin can tick the multi-office checkbox (shown for this office and editable for this admin). */
  get canOptInMultiOffice(): boolean {
    const control = this.group && getControl(this.group, 'allowAccessToMultipleOffices');
    return Boolean(control) && toAppFormControl(control).visible && control.enabled;
  }

  get submitValue(): UserDetails {
    // Grant mode only starts when multi-office access is ticked, so the flag is always the admin's own choice.
    // The backend rejects membership combined with legacy email access.
    const allowAccessToMultipleOffices = getControlValue(this.group, 'allowAccessToMultipleOffices');
    const allowEmailAccessToMultipleOffices = !this.isGrantMode
      && getControlValue(this.group, 'allowEmailAccessToMultipleOffices');
    const systemRoles = { ...getControlValue(this.group, 'systemRoles') };

    if (this.isGrantMode) {
      systemRoles[UserSystemRole.Corporate] = false;
      systemRoles[UserSystemRole.Master] = false;
    }

    return {
      ...this.group.value,
      systemRoles,
      status: this.isGrantMode ? UserStatus.Active : getControlValue(this.group, 'status'),
      userEmail: getControlValue(this.group, 'userEmail'),
      allowAccessToLeads: getControlValue(this.group, 'allowAccessToLeads'),
      leadsOf: getControlValue(this.group, 'leadsOf'),
      allowAccessToMultipleOffices,
      allowEmailAccessToMultipleOffices,
      // Legacy email flow only: when email-access is off, keep original sendEmailsFrom on payload mapping.
      sendEmailsFrom: allowEmailAccessToMultipleOffices
        ? getControlValue(this.group, 'sendEmailsFrom')
        : this.originalSendEmailsFrom,
    };
  }

  build(user: UserDetails): void {
    this.originalSendEmailsFrom = user.sendEmailsFrom;
    this.grantExistingUser = void 0;
    this.detectedExistingUser = void 0;

    this.initSchema(user);
    this.originalUserEmail = getControlValue(this.group, 'userEmail');

    if (user.status === UserStatus.Transferred) {
      getControl(this.group, 'inboxTo').enable();
    }
  }

  setContact(contact: UserDetailsContact): void {
    if (this.createMode) {
      setControlValue(this.group, 'contact', contact);
      setControlValue(this.group, 'userEmail', contact ? getNormalizedEmailName(contact) : '');
      validateControlValue(this.group, 'userEmail');
      markControlAsDirty(this.group, 'userEmail');
      markControlAsTouched(this.group, 'userEmail');
    } else {
      const oldContact = getControlValue(this.group, 'contact');
      setControlValue(this.group, 'contact', contact);
      if (isNil(oldContact) || oldContact.id !== contact.id) {
        markControlAsDirty(this.group, 'contact');
      }
    }
  }

  getContactId(): UserDetailsContact {
    return getControlValue(this.group, 'contact');
  }

  checkLogin(value: string): Observable<Nullable<ValidationErrors>> {
    if (this.viewMode) {
      return of$(nullate(void 0));
    }
    const existingUser = getControlValue(this.group, 'existingUser');
    if (existingUser) {
      return of$(nullate(void 0));
    }

    if (isEmpty(value)) {
      this.detectedExistingUser = void 0;
      this.exitGrantMode();
      return of$(nullate(void 0));
    }

    // Edit mode: only re-validate when the login email actually changed.
    // Avoids a redundant /validate-login after create → navigate to edit.
    if (!this.createMode && value === this.originalUserEmail) {
      return of$(nullate(void 0));
    }

    return <Observable<Nullable<ValidationErrors>>>this.businessService.validateLogin(
      value,
      getControlValue(this.group, 'userId'),
    ).pipe(
      map$((response: Maybe<UserValidateLogin>) => {
        const password = getControl(this.group, 'password');

        if (this.createMode && this.userContext.officeView && response?.valid && response.existingUser && response.user
          && !this.isGrantMode && getControlValue(this.group, 'contact')) {
          // A contact of this office was picked and its email is another office's active login: no grant offer,
          // the admin has to change the email.
          this.detectedExistingUser = void 0;
          return this.activeAccountErrors(response.user);
        }

        if (this.createMode && this.userContext.officeView && response?.valid && response.existingUser && response.user) {
          // Login belongs to another office's employee. Grant only when the admin ticked multi-office access;
          // otherwise keep the normal form and block saving with a message explaining how to grant access.
          this.detectedExistingUser = response.user;
          if (this.allowAccessToMultipleOffices) {
            this.enterGrantMode(response.user);
            return nullate(void 0);
          }
          this.exitGrantMode();
          return this.multiOfficeOptInErrors(response.user);
        }

        this.detectedExistingUser = void 0;
        if (this.createMode) {
          this.exitGrantMode();
          if (response?.transferred) {
            password.reset();
            password.disable();
          } else {
            password.enable();
          }
        }
        return nullate(void 0);
      }),
      catchError$((basket: MessageBasket) => {
        this.detectedExistingUser = void 0;
        this.exitGrantMode();
        return of$(basketToFormErrors(basket));
      }));
  }

  /**
   * Called when "Allow this user to sign in to more than one office" is ticked or unticked: enter or leave the grant
   * path for an already detected existing user, and refresh the login field's validation message.
   */
  changeAllowAccessToMultipleOffices(): void {
    if (!this.detectedExistingUser) {
      return;
    }
    if (this.allowAccessToMultipleOffices) {
      this.enterGrantMode(this.detectedExistingUser);
    } else {
      this.exitGrantMode();
    }
    validateControlValue(this.group, 'userEmail');
  }

  private multiOfficeOptInErrors(user: UserValidateLoginExistingUser): Nullable<ValidationErrors> {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.userEmail;
    const params = { name, office: user.homeOfficeName || '' };
    // Admin can tick the checkbox here → tell them to; otherwise point them to someone who can.
    const key = this.canOptInMultiOffice ? 'msg_user_exists_enable_multi_office' : 'msg_user_exists_in_other_office';
    return nullate(basketToFormErrors(MessageBasket.error(key, params)));
  }

    /** Short "login taken" under the field; the details (who and which office) go to an error notification. */
  private activeAccountErrors(user: UserValidateLoginExistingUser): Nullable<ValidationErrors> {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.userEmail;
    this.notificationService.error('msg_user_email_has_active_account', { name, office: user.homeOfficeName || '' });
    return nullate(basketToFormErrors(MessageBasket.error('msg_user_login_taken')));
  }
  
  enterGrantMode(user: UserValidateLoginExistingUser): void {
    this.grantExistingUser = user;
    const contact = getControl(this.group, 'contact');
    const password = getControl(this.group, 'password');
    contact.reset();
    contact.disable();
    password.reset();
    password.disable();
    this.cdr.markForCheck();
  }

  exitGrantMode(): void {
    if (!this.grantExistingUser) {
      return;
    }
    this.grantExistingUser = void 0;
    if (this.createMode && !getControlValue(this.group, 'existingUser')) {
      getControl(this.group, 'contact').enable();
      getControl(this.group, 'password').enable();
    }
    this.cdr.markForCheck();
  }

  updateAllowAccessToLeads(): void {
    const leadsOfControl = toAppFormGroup(getControl(this.group, 'leadsOf'));
    const allowAccessSalespersonLeads = this.allowAccessToLeads === UserAllowAccess.SalespersonLeads;
    leadsOfControl.updateSchema({ visible: allowAccessSalespersonLeads });
  }

  resetAllowAccessToLeads(): void {
    if (!this.isSalesPerson && this.allowAccessToLeads !== UserAllowAccess.OwnLeads) {
      setControlValue(this.group, 'allowAccessToLeads', UserAllowAccess.OwnLeads);
    }
  }

  changeSystemRole(): void {
    const salesPerson: boolean = this.isSalesPerson;
    const statusActive = getControlValue(this.group, 'status') === UserStatus.Active;
    const allowAccessToLeads = getControl(this.group, 'allowAccessToLeads');

    if (salesPerson && statusActive) {
      allowAccessToLeads.enable();
    }

    if (!salesPerson || !statusActive) {
      allowAccessToLeads.reset();
      allowAccessToLeads.disable();
    }

    this.updateAllowAccessToLeads();
    this.resetAllowAccessToLeads();
  }

  existingUserCheck(isExistingUserChecked: boolean): void {
    const fieldsToToggle = ['contact', 'password'];

    fieldsToToggle.forEach(field => {
      const control = getControl(this.group, field);
      if (isExistingUserChecked) {
        control.disable();
      } else {
        control.enable();
      }
    });
  }

  disableStatusList(): void {
    const status = getControl(this.group, 'status');
    status.disable();
  }
  disableUserDataToTransfer(): void {
    const userDataToTransfer = getControl(this.group, 'userDataToTransfer');
    userDataToTransfer.disable();
    userDataToTransfer.reset();
  }

  enableUserDataToTransfer(): void {
    const userDataToTransfer = getControl(this.group, 'userDataToTransfer');
    userDataToTransfer.enable();
  }

  changeStatus(control: any): void {
    if ([UserStatus.Inactive, UserStatus.Transferred].includes(control.value)) {
      this.setReassignToSalesFieldEnabled();
    } else {
      this.setReassignToSalesFieldDisabled();
    }
    const inboxTo = getControl(this.group, 'inboxTo');
    const systemRoles = getControl(this.group, 'systemRoles');
    const allowAccessToLeads = getControl(this.group, 'allowAccessToLeads');
    const leadsOf = getControl(this.group, 'leadsOf');

    inboxTo.disable();
    systemRoles.enable();

    if (systemRoles.value[UserSystemRole.Salesperson]) {
      allowAccessToLeads.enable();
      leadsOf.enable();
    }
    if (control.value === UserStatus.Transferred || control.value === UserStatus.Inactive || control.value === UserStatus.Deleted) {
      inboxTo.enable();
      allowAccessToLeads.disable();
      systemRoles.disable();
      leadsOf.disable();
    }
    if (control.value === UserStatus.Active) {
      inboxTo.reset();
    }
  }

  protected setReassignToSalesFieldEnabled(): void {
    getControl(this.group, 'targetSalespersonIds').enable();
    getControl(this.group, 'useRoundRobin').enable();
  }

  protected setReassignToSalesFieldDisabled(): void {
    const targetSalespersonIds = getControl(this.group, 'targetSalespersonIds');
    const useRoundRobin = getControl(this.group, 'useRoundRobin');
    targetSalespersonIds.disable();
    targetSalespersonIds.reset([]);
    useRoundRobin.disable();
    useRoundRobin.reset(false);
  }

  protected buildSchema(user: UserDetails, { root, group }: FormStateSchema): FormRootSchema {
    const mode = isRouteForCreationMode(this.route) ? FormMode.CreateMode
      : (!this.userContext.hasPermission(BuilderPermission.Setup.Manager.UserDetails.EDIT)
      || (user.status === UserStatus.Deleted || user.status === UserStatus.Transferred)
        ? FormMode.ViewMode : FormMode.EditMode);

    const isActive = user.status === UserStatus.Active;

    return root({
      mode,
      optimisticLock: true,
      enabled: whenCreateOrEditMode(),
      privilegedEntryPoints: [
        PRIVILEGED_EP_USER_UPDATE,
        PRIVILEGED_EP_USER_VERIFICATION,
        PRIVILEGED_EP_USER_SEND_EMAILS_FROM_VERIFICATION,
      ],
      confirmations: {
        skipChangeStatusCheck: false,
      },
      children: {
        userId: void 0,
        status: { enabled: whenEditMode() },
        contact: { required: true },
        userEmail: {
          required: true,
          type: TextTypes.GoogleUserName,
          asyncValidators: [(control: AbstractControl) => of$(true).pipe(
            delay$(TYPING_DELAY),
            switchMap$(() => this.checkLogin(control.value)))],
          privileged: PRIVILEGED_EP_USER_UPDATE,
          whenPrivileged: 'none',
        },
        inboxTo: {
          enabled: whenAllOf(
            whenEditMode(),
            constant(user.status === UserStatus.Inactive)),
        },
        systemRoles: group({
          enabled: [whenOfficeView(), isActive],
          children: mapValues(user.systemRoles, constant(void 0)),
        }),
        sendEmailsFrom: {
          visible: whenAllOf(
            whenOneOf(whenSwitchedUser(), whenHasSystemRole(SystemRoleBit.Franchisee)),
            whenCreateOrEditMode(),
            coalesce(user.allowEmailAccessToMultipleOffices, false)),
          type: TextTypes.GoogleUserName,
          required: true,
          privileged: PRIVILEGED_EP_USER_SEND_EMAILS_FROM_VERIFICATION,
        },
        allowAccessToMultipleOffices: {
          visible: user.usersFromOtherOfficesAccess,
          // Granted on create only; on an existing user the value is loaded from its memberships and shown read-only
          // (access is removed by inactivating / transferring the user in that office, not by unticking).
          enabled: whenAllOf(whenCreateMode(), whenOneOf(whenSwitchedUser(), whenHasSystemRole(SystemRoleBit.Franchisee))),
          required: true,
        },
        allowEmailAccessToMultipleOffices: {
          visible: coalesce(user.usersEmailAccessFromOtherOffices, false),
          enabled: whenOneOf(whenSwitchedUser(), whenHasSystemRole(SystemRoleBit.Franchisee)),
          required: true,
        },
        publishToWeb: { visible: whenOneOf(whenOfficeUser(), whenSwitchedUser()) },
        usersFromOtherOfficesAccess: void 0,
        usersEmailAccessFromOtherOffices: void 0,
        password: { required: true, type: MaskedTypes.GoogleUserPassword, enabled: whenCreateMode() },
        roleId: { required: whenOfficeView() },
        position: { required: whenOfficeView() },
        allowAccessToLeads: {
          visible: whenOfficeView(),
          enabled: [coalesce(user.systemRoles[UserSystemRole.Salesperson], true), isActive],
        },
        leadsOf: group({
          enabled: isActive,
          visible: user.allowAccessToLeads === UserAllowAccess.SalespersonLeads,
          children: mapValues(user.leadsOf, constant(void 0)),
        }),
        targetSalespersonIds: {
          visible: whenAllOf(coalesce(user.systemRoles[UserSystemRole.Salesperson], false), constant(!!user.hasAssignedLeads)),
          enabled: whenAllOf(coalesce(user.systemRoles[UserSystemRole.Salesperson], false),
                             constant(!!user.hasAssignedLeads),
                             (user.status === UserStatus.Transferred || user.status === UserStatus.Inactive)),
          required: whenAllOf(coalesce(user.systemRoles[UserSystemRole.Salesperson], false), constant(!!user.hasAssignedLeads)),
        },
        useRoundRobin: {
          visible: whenAllOf(coalesce(user.systemRoles[UserSystemRole.Salesperson], false), constant(!!user.hasAssignedLeads)),
          enabled: whenAllOf(coalesce(user.systemRoles[UserSystemRole.Salesperson], false),
                             constant(!!user.hasAssignedLeads),
                             (user.status === UserStatus.Transferred || user.status === UserStatus.Inactive)),
        },
        userDataToTransfer: {
          enabled: false,
        },
        // Chosen on create only; on an existing user the saved value is shown read-only
        existingUser: { enabled: whenCreateMode() }
      },
    });
  }
}
